import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, TrendingUp, TrendingDown, Minus, DollarSign, BarChart3, Target, Shield } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function PriceChange({ value }) {
  if (!value) return <span className="text-white/30">-</span>;
  const isUp = value > 0;
  const Icon = isUp ? TrendingUp : value < 0 ? TrendingDown : Minus;
  const color = isUp ? 'text-emerald-400' : value < 0 ? 'text-red-400' : 'text-white/40';
  return (
    <span className={`font-mono text-xs flex items-center gap-1 ${color}`}>
      <Icon className="w-3 h-3" />
      {isUp ? '+' : ''}{value.toFixed(2)}%
    </span>
  );
}

function MiniSparkline({ data, color = '#0ea5e9' }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const w = 60, h = 20;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`).join(' ');
  return (
    <svg width={w} height={h} className="inline-block ml-2">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" opacity="0.7" />
    </svg>
  );
}

function formatVolume(v) {
  if (!v) return '-';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v}`;
}

export default function MarketsPanel({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('all');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/markets/all`, authHeaders);
      setData(res.data);
    } catch (err) {
      setError('Erreur de chargement des marches');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, []);

  // Auto refresh every 3 min
  useEffect(() => {
    const interval = setInterval(fetchData, 180000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const predictions = data?.predictions?.markets || [];
  const crypto = data?.crypto?.coins || [];
  const stocks = data?.defense?.stocks || [];
  const indices = data?.defense?.indices || [];

  return (
    <div className="space-y-4 p-4" data-testid="markets-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-marine-cyan text-lg uppercase tracking-wider flex items-center gap-2">
          <BarChart3 className="w-5 h-5" /> Marches Financiers & Predictions
        </h2>
        <div className="flex items-center gap-2">
          <div className="flex border border-marine-slate/30">
            {[
              { id: 'all', label: 'Tout' },
              { id: 'defense', label: 'Defense' },
              { id: 'crypto', label: 'Crypto' },
              { id: 'predictions', label: 'Predictions' },
            ].map(s => (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                className={`px-3 py-1 font-mono text-xs uppercase tracking-wider transition-colors ${activeSection === s.id ? 'bg-marine-cyan/20 text-marine-cyan' : 'text-white/40 hover:text-white/60'}`}
                data-testid={`markets-filter-${s.id}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-1.5 border border-marine-slate/50 text-white/60 hover:text-marine-cyan hover:border-marine-cyan"
            data-testid="refresh-markets"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && <div className="bg-red-900/30 border border-red-500/50 p-3 font-mono text-red-400 text-sm">{error}</div>}

      {/* Defense Stocks */}
      {(activeSection === 'all' || activeSection === 'defense') && (
        <div className="border border-marine-slate/30 bg-black/40">
          <div className="px-4 py-2 border-b border-marine-slate/30 flex items-center gap-2">
            <Shield className="w-4 h-4 text-sky-400" />
            <span className="font-heading text-sky-400 text-sm uppercase tracking-wider">Defense Contractors & Indices</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-0">
            {stocks.map(s => (
              <div key={s.symbol} className="px-4 py-3 border-b border-r border-marine-slate/20 hover:bg-white/5 transition-colors" data-testid={`stock-${s.symbol}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-white/90 text-sm font-bold">{s.symbol}</span>
                  <PriceChange value={s.change_pct} />
                </div>
                <div className="font-mono text-white text-lg">${s.price?.toLocaleString()}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-mono text-white/30 text-xs">{s.name}</span>
                  <MiniSparkline data={s.closes_5d} color={s.change_pct >= 0 ? '#10b981' : '#ef4444'} />
                </div>
              </div>
            ))}
            {indices.map(s => (
              <div key={s.symbol} className="px-4 py-3 border-b border-r border-marine-slate/20 bg-marine-slate/5 hover:bg-white/5 transition-colors" data-testid={`index-${s.symbol}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-marine-amber/90 text-sm font-bold">{s.name}</span>
                  <PriceChange value={s.change_pct} />
                </div>
                <div className="font-mono text-white text-lg">{s.price?.toLocaleString()}</div>
                <MiniSparkline data={s.closes_5d} color={s.change_pct >= 0 ? '#10b981' : '#ef4444'} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Crypto */}
      {(activeSection === 'all' || activeSection === 'crypto') && (
        <div className="border border-marine-slate/30 bg-black/40">
          <div className="px-4 py-2 border-b border-marine-slate/30 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-amber-400" />
            <span className="font-heading text-amber-400 text-sm uppercase tracking-wider">Crypto</span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-0">
            {crypto.map(c => (
              <div key={c.id} className="px-4 py-3 border-b border-r border-marine-slate/20 hover:bg-white/5 transition-colors" data-testid={`crypto-${c.symbol}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-amber-400/90 text-sm font-bold">{c.symbol}</span>
                  <PriceChange value={c.change_24h} />
                </div>
                <div className="font-mono text-white text-lg">${c.price?.toLocaleString()}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className="font-mono text-white/30 text-xs">{c.name}</span>
                  <span className="font-mono text-white/30 text-xs">Vol: {formatVolume(c.volume_24h)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Prediction Markets */}
      {(activeSection === 'all' || activeSection === 'predictions') && (
        <div className="border border-marine-slate/30 bg-black/40">
          <div className="px-4 py-2 border-b border-marine-slate/30 flex items-center gap-2">
            <Target className="w-4 h-4 text-violet-400" />
            <span className="font-heading text-violet-400 text-sm uppercase tracking-wider">
              Marches de Prediction (Polymarket)
            </span>
            <span className="font-mono text-white/30 text-xs ml-auto">{predictions.length} marches geopolitiques</span>
          </div>
          <div className="divide-y divide-marine-slate/20 max-h-96 overflow-y-auto">
            {predictions.length === 0 && (
              <div className="p-4 text-center text-white/40 font-mono text-xs">
                {loading ? 'Chargement des marches de prediction...' : 'Aucun marche geopolitique actif'}
              </div>
            )}
            {predictions.map((m, i) => (
              <a
                key={m.id || i}
                href={m.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 px-4 py-3 hover:bg-white/5 transition-colors"
                data-testid={`prediction-${i}`}
              >
                {/* Probability bar */}
                <div className="w-16 flex-shrink-0">
                  <div className="relative h-6 bg-marine-slate/30 overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 transition-all"
                      style={{
                        width: `${m.yes_price}%`,
                        background: m.yes_price > 70 ? '#10b981' : m.yes_price > 40 ? '#f59e0b' : '#ef4444',
                      }}
                    />
                    <span className="absolute inset-0 flex items-center justify-center font-mono text-xs text-white font-bold">
                      {m.yes_price}%
                    </span>
                  </div>
                </div>
                {/* Question */}
                <div className="flex-1 min-w-0">
                  <p className="font-mono text-white/90 text-sm truncate">{m.question}</p>
                  {m.description && <p className="font-mono text-white/30 text-xs truncate">{m.description}</p>}
                </div>
                {/* Volume */}
                <div className="flex-shrink-0 text-right">
                  <span className="font-mono text-white/40 text-xs">Vol: {formatVolume(m.volume)}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
